interface ListItem {
  label?: string;
  text: string;
}

interface LegalSection {
  heading: string;
  content: string[];
  listItems?: ListItem[];
  additionalContent?: string;
}

interface LegalDocument {
  title: string;
  sections: LegalSection[];
  lastUpdated: string;
}

export const LegalContent: { privacy: LegalDocument; termsOfService: LegalDocument } = {
  privacy: {
    title: "Privacy Policy",
    sections: [
      {
        heading: "Our Research Mission",
        content: [
          "The HOLE Foundation is dedicated to advancing government accountability and transparency through research, technology, and public advocacy. As part of our mission, we collect and analyze data about how citizens interact with our tools and the challenges they face in accessing public records.",
          "This research helps us understand barriers to transparency, improve our AI-powered tools, and contribute to the broader conversation about government accountability and legal ethics."
        ]
      },
      {
        heading: "What Data We Collect",
        content: [
          "We collect the following categories of data:"
        ],
        listItems: [
          { label: "User Actions", text: "Pages visited, buttons clicked, state selections, and FOIA exemptions reviewed" },
          { label: "FOIA Requests", text: "Information about FOIA requests generated through our platform (description, state, agency, request type)" },
          { label: "AI Agent Metrics", text: "Data about how our AI agent performs and whether requests meet user expectations" },
          { label: "Session Information", text: "Anonymized session IDs, timestamps, and browser information" },
          { label: "Request Analysis", text: "Data patterns that help us improve our AI tools and identify emerging transparency trends" }
        ]
      },
      {
        heading: "Why We Collect This Data",
        content: [
          "We collect this data to advance our research mission and improve our services:"
        ],
        listItems: [
          { label: "Training Our AI Agent", text: "Understanding how users interact with FOIA requests helps us improve response quality and accuracy" },
          { label: "Identifying Patterns", text: "Analyzing request trends helps us spot recurring barriers to transparency and emerging issues" },
          { label: "Supporting Research", text: "We use anonymized data to publish research on government accountability and transparency challenges" },
          { label: "Service Improvement", text: "Usage analytics help us enhance user experience and platform functionality" },
          { label: "Legal Accountability", text: "We track court cases and litigation outcomes related to transparency issues" }
        ]
      },
      {
        heading: "Your Privacy Rights & Controls",
        content: [
          "We respect your privacy and provide granular controls over data collection through our Cookie Banner:"
        ],
        listItems: [
          { label: "Analytics Consent", text: "Control whether we track page views, state selections, and exemption reviews" },
          { label: "Research Consent", text: "Control whether your FOIA requests and AI agent feedback can be used for research purposes" },
          { label: "Marketing Consent", text: "Control whether we send you updates about our research and foundation activities" },
          { label: "Functional Consent", text: "Essential features that keep the platform running (cannot be disabled)" }
        ],
        additionalContent: "You can modify your preferences at any time by clicking the cookie preferences link in the footer or clearing your browser cookies."
      },
      {
        heading: "Data Protection & Anonymization",
        content: [
          "We implement industry-standard protections for your data:"
        ],
        listItems: [
          { label: "IP Hashing", text: "We hash your IP address so we cannot connect actions to your real identity" },
          { label: "Session-Based Tracking", text: "We use anonymous session IDs instead of user IDs to track user journeys" },
          { label: "Data Retention", text: "Research data is retained for analysis but user identifiers are separated from behavioral data" },
          { label: "No Third-Party Sharing", text: "We do not sell or share personal data with third parties (except as required by law)" }
        ]
      },
      {
        heading: "GDPR & Legal Compliance",
        content: [
          "The HOLE Foundation is committed to complying with all applicable privacy laws, including GDPR for EU residents:"
        ],
        listItems: [
          { label: "Lawful Basis", text: "We process data based on your consent and our legitimate interests in advancing transparency research" },
          { label: "Right to Access", text: "You can request a copy of all personal data we hold about you" },
          { label: "Right to Deletion", text: "You can request deletion of your personal data (subject to legal retention requirements)" },
          { label: "Right to Withdrawal", text: "You can withdraw consent at any time using our cookie preferences" },
          { label: "Data Portability", text: "You can request your data in a structured format" }
        ]
      },
      {
        heading: "Court Cases & Litigation",
        content: [
          "The HOLE Foundation tracks significant court cases and litigation related to government transparency, FOIA enforcement, and public records. This data helps us:",
          "- Identify patterns in how courts interpret transparency laws",
          "- Provide users with context about jurisdiction-specific legal challenges",
          "- Support legal research and advocacy efforts",
          "We only track publicly available court information and do not collect personal data about litigants."
        ]
      },
      {
        heading: "Contact & Data Subject Requests",
        content: [
          "If you have questions about our privacy practices or wish to exercise your rights under GDPR or other privacy laws, please contact:",
          "HOLE Foundation",
          "Privacy Inquiry",
          "privacy@holefoundation.org"
        ]
      },
      {
        heading: "Updates to This Policy",
        content: [
          "We may update this Privacy Policy from time to time. When we make material changes, we will notify you by updating the \"Last Updated\" date below and, if required, by obtaining your renewed consent."
        ]
      }
    ],
    lastUpdated: "January 2025"
  },
  termsOfService: {
    title: "Terms of Service",
    sections: [
      {
        heading: "Acceptance of Terms",
        content: [
          "By accessing and using the HOLE Foundation's website and tools, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service."
        ]
      },
      {
        heading: "License & Usage Rights",
        content: [
          "The HOLE Foundation grants you a limited, non-exclusive, non-transferable license to use our tools and content for lawful purposes. You may not:"
        ],
        listItems: [
          { text: "Reverse engineer, decompile, or disassemble any software" },
          { text: "Use automated tools to scrape or extract data without permission" },
          { text: "Attempt to gain unauthorized access to our systems" },
          { text: "Use our tools for illegal purposes or to interfere with government operations" },
          { text: "Modify, reproduce, or redistribute our content without permission" }
        ]
      },
      {
        heading: "Intellectual Property",
        content: [
          "All content, software, and materials on our platform are the property of the HOLE Foundation or our licensors. This includes text, graphics, logos, images, audio clips, and source code. They are protected by international copyright laws.",
          "Much of our software is open source and available under applicable open source licenses. Please review the specific license terms for each project."
        ]
      },
      {
        heading: "User-Generated Content",
        content: [
          "When you submit FOIA requests, feedback, or other content through our platform, you grant the HOLE Foundation a worldwide, non-exclusive license to use, copy, modify, and publish this content as part of our research and platform operations.",
          "This does not grant us rights to any personal information you may include, which is governed by our Privacy Policy."
        ]
      },
      {
        heading: "Disclaimer of Warranties",
        content: [
          "Our platform is provided on an \"as is\" and \"as available\" basis. We make no warranties, express or implied, regarding:"
        ],
        listItems: [
          { text: "The accuracy, completeness, or reliability of any information" },
          { text: "That the platform will meet your requirements" },
          { text: "That the platform will be uninterrupted or error-free" },
          { text: "That defects will be corrected" }
        ],
        additionalContent: "Important Legal Notice: We are not lawyers and do not provide legal advice. Our FOIA templates and guidance are educational resources only. Always consult with a qualified attorney for specific legal advice regarding your situation."
      },
      {
        heading: "Limitation of Liability",
        content: [
          "To the maximum extent permitted by law, the HOLE Foundation shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or use, even if advised of the possibility of such damages."
        ]
      },
      {
        heading: "Indemnification",
        content: [
          "You agree to indemnify, defend, and hold harmless the HOLE Foundation, its officers, directors, and employees from any claims, damages, or costs (including reasonable attorneys' fees) arising from your use of our platform or violation of these terms."
        ]
      },
      {
        heading: "Governing Law",
        content: [
          "These terms and conditions are governed by and construed in accordance with the laws of the jurisdiction in which the HOLE Foundation is registered, and you irrevocably submit to the exclusive jurisdiction of the courts in that location."
        ]
      },
      {
        heading: "Severability",
        content: [
          "If any provision of these terms is found to be invalid or unenforceable, that provision shall be modified to the minimum extent necessary to make it valid, and the remaining provisions shall continue in full force and effect."
        ]
      }
    ],
    lastUpdated: "January 2025"
  }
};
